import { useMPEFetch, useMutateMPEFetch } from '@retail/app/hooks';
import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useAuthContext } from '@retail/auth/data-access';
import { mpeApi } from '@retail/mgpris/config';
import { AssortmentTagDTO, PostAssortmentTagDto } from '@retail/my-assortment/types';
import { QueryOptions } from '@shared/fetch-utils';
import { buildUrl } from '@shared/utils';

interface AssortmentTagsProps extends QueryOptions {
  assortmentIds: number[];
}

export function useFetchAssortmentTags({ assortmentIds, ...queryOptions }: AssortmentTagsProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<AssortmentTagDTO[]>({
    method: 'GET',
    queryKey: ['assortment-tags', assortmentIds],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.assortmentTags.tags.getFullLinkPath(),
      searchParams: { assortmentIds },
    }),
    ...queryOptions,
  });
}

interface CreateProps extends QueryOptions {
  assortmentId: number;
}

export function useCreateAssortmentTags({ assortmentId, ...queryOptions }: CreateProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, PostAssortmentTagDto>({
    method: 'POST',
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.assortmentTags.v2.fullRoutePath,
      searchParams: { assortmentId },
    }),
    token,
    ...queryOptions,
  });
}

export const useDeleteAssortmentTags = (queryOptions: QueryOptions = {}) => {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void>({
    url: (tagId: number) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.assortmentTags.tagId.getFullLinkPath({ tagId }),
      }),
    method: 'DELETE',
    token,
    ...queryOptions,
  });
};

export const useUpdateAssortmentTags = (queryOptions: QueryOptions = {}) => {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<
    void,
    { sendEvents?: boolean; visibleToMembers?: boolean; selectableByMembers?: boolean }
  >({
    url: (tagId: number) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.assortmentTags.tagId.getFullLinkPath({ tagId }),
      }),
    method: 'PUT',
    token,
    ...queryOptions,
  });
};
